/**
 * @module bridges
 * @package @geenius/adapters/react-ant
 * @description Ant Design bridge components for adapter loading, error, and
 * notification surfaces. Adapter contracts stay delegated to the React
 * reference variant; this module only owns Ant rendering affordances.
 */

"use client";

import Alert from "antd/lib/alert/index.js";
import Button from "antd/lib/button/index.js";
import notification, {
  type ArgsProps as NotificationArgsProps,
} from "antd/lib/notification/index.js";
import Progress, {
  type ProgressProps,
} from "antd/lib/progress/index.js";
import Space from "antd/lib/space/index.js";
import Spin, { type SpinProps } from "antd/lib/spin/index.js";
import Typography from "antd/lib/typography/index.js";
import {
  Component,
  type ErrorInfo,
  type JSX,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";

type LoadingBridgeVariant = "spin" | "progress";
type ToastBridgePlacement = NonNullable<NotificationArgsProps["placement"]>;

function normalizePercent(progress: number | undefined): number | undefined {
  if (progress === undefined) {
    return undefined;
  }

  return Math.min(100, Math.max(0, progress));
}

export interface LoadingBridgeProps {
  loading?: boolean;
  isLoading?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  progress?: number;
  variant?: LoadingBridgeVariant;
  children?: ReactNode;
  className?: string;
  spinProps?: SpinProps;
  progressProps?: ProgressProps;
}

/**
 * Renders an Ant Design loading affordance for async adapter operations.
 */
export function LoadingBridge({
  loading,
  isLoading,
  label = "Loading adapters",
  description,
  progress,
  variant = "spin",
  children = null,
  className,
  spinProps,
  progressProps,
}: LoadingBridgeProps): JSX.Element {
  const active = isLoading ?? loading ?? true;

  if (!active) {
    return <>{children}</>;
  }

  const percent = normalizePercent(progress);
  const ariaLabel = typeof label === "string" ? label : "Loading adapters";

  return (
    <Space
      aria-label={ariaLabel}
      aria-busy="true"
      aria-live="polite"
      className={className}
      direction="vertical"
      role="status"
      size={8}
    >
      {variant === "progress" ? (
        <Progress
          {...progressProps}
          aria-label={ariaLabel}
          percent={percent ?? 100}
          status={percent === undefined ? "active" : progressProps?.status}
        />
      ) : (
        <Spin {...spinProps} />
      )}
      {label ? <Typography.Text>{label}</Typography.Text> : null}
      {description ? (
        <Typography.Text type="secondary">{description}</Typography.Text>
      ) : null}
    </Space>
  );
}

type ErrorBoundaryFallback =
  | ReactNode
  | ((error: Error, reset: () => void) => ReactNode);

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Adapter bridge error");
}

export interface ErrorBoundaryBridgeProps {
  children?: ReactNode;
  error?: unknown;
  fallback?: ErrorBoundaryFallback;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  resetLabel?: ReactNode;
  className?: string;
}

interface ErrorBoundaryBridgeState {
  caughtError: Error | null;
}

/**
 * Catches React render failures and displays them with Ant alert primitives.
 */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = { caughtError: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryBridgeState {
    return { caughtError: normalizeError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(normalizeError(error), info);
  }

  private reset = (): void => {
    this.props.onReset?.();
    this.setState({ caughtError: null });
  };

  render(): ReactNode {
    const error =
      this.props.error !== undefined
        ? normalizeError(this.props.error)
        : this.state.caughtError;

    if (!error) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Alert
        action={
          <Button onClick={this.reset} size="small" type="primary">
            {this.props.resetLabel ?? "Retry"}
          </Button>
        }
        className={this.props.className}
        description={
          <Space direction="vertical" size={4}>
            {this.props.description ? (
              <Typography.Text>{this.props.description}</Typography.Text>
            ) : null}
            <Typography.Text>{error.message}</Typography.Text>
          </Space>
        }
        message={this.props.title ?? "Adapter error"}
        role="alert"
        showIcon
        type="error"
      />
    );
  }
}

export type ToastBridgeStatus =
  | "default"
  | "info"
  | "success"
  | "warning"
  | "error";

export interface ToastBridgeProps {
  children?: ReactNode;
  open?: boolean;
  message?: ReactNode;
  description?: ReactNode;
  status?: ToastBridgeStatus;
  toastKey?: string;
  duration?: number;
  placement?: ToastBridgePlacement;
  onClose?: () => void;
  onShown?: () => void;
  notificationProps?: Omit<
    NotificationArgsProps,
    | "description"
    | "duration"
    | "key"
    | "message"
    | "onClose"
    | "placement"
    | "type"
  >;
}

function toastNodeKey(value: ReactNode): string {
  if (value === null || value === undefined || typeof value === "boolean") {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return "react-node";
}

/**
 * Emits Ant Design notifications declaratively while rendering optional
 * children and the required notification context holder.
 */
export function ToastBridge({
  children,
  open = false,
  message,
  description,
  status = "default",
  toastKey,
  duration,
  placement = "bottomRight",
  onClose,
  onShown,
  notificationProps,
}: ToastBridgeProps): JSX.Element {
  const [api, contextHolder] = notification.useNotification();
  const emittedKeyRef = useRef<string | null>(null);
  const dedupeKey = useMemo(
    () =>
      toastKey ??
      [status, toastNodeKey(message), toastNodeKey(description)].join(":"),
    [description, message, status, toastKey],
  );

  useEffect(() => {
    if (!open) {
      emittedKeyRef.current = null;
      return;
    }

    if (message === undefined || message === null) {
      return;
    }

    if (emittedKeyRef.current === dedupeKey) {
      return;
    }

    emittedKeyRef.current = dedupeKey;
    api.open({
      ...notificationProps,
      description,
      duration,
      key: dedupeKey,
      message,
      onClose,
      placement,
      type: status === "default" ? undefined : status,
    });
    onShown?.();
  }, [
    api,
    dedupeKey,
    description,
    duration,
    message,
    notificationProps,
    onClose,
    onShown,
    open,
    placement,
    status,
  ]);

  return (
    <>
      {contextHolder}
      {children}
    </>
  );
}
